import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { PalletDetailDto, PalletDto } from 'src/store/pallets/pallet';
import { mapPalletDetailToEdit } from 'src/store/pallets/pallet.map';
import {
  createPallet,
  deletePallet,
  editPallet,
  fetchPallets
} from 'src/store/pallets/pallets.actions';
import { PalletApi } from 'src/store/pallets/pallets.api';
import { selectPallets } from 'src/store/pallets/pallets.selectors';
import { isLoadingPallets } from 'src/store/requests/requests.selectors';
import { PalletForm } from './PalletForm';

export const PalletsPage = () => {
  const [currentPallet, setCurrentPallet] = useState<PalletDto | null>(null);
  const [pallet, setPallet] = useState<Nullable<PalletDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();

  const dispatch = useReduxDispatch();

  const pallets = useSelector(selectPallets);
  const loadingPallets = useSelector(isLoadingPallets);

  useEffect(() => {
    dispatch(fetchPallets());
  }, [dispatch]);

  useEffect(() => {
    if (currentPallet) {
      setShowDeleteModal(true);
    }
  }, [currentPallet]);

  const onCancel = () => {
    setCurrentPallet(null);
    setPallet(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (pallet: PalletDto) => {
    const response = await PalletApi.getById(pallet.id);
    if (response.ok) {
      setPallet(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (pallet: PalletDto) => {
    setCurrentPallet(pallet);
  };

  const onConfirmDelete = () => {
    if (currentPallet) {
      dispatch(deletePallet(currentPallet.id, currentPallet));
      setCurrentPallet(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (pallet: PalletDetailDto) => {
    const dto = mapPalletDetailToEdit(pallet);
    if (dto.id) {
      await dispatch(editPallet(dto.id, dto));
      onCancel();
    } else {
      await dispatch(createPallet(dto));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('pallet.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('pallet.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('pallet.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={pallets}
        loading={loadingPallets}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          width={200}
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell count={pallets?.length} label={t('pallet.plural')} />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column field="description" title={t('props.description')} />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${pallet ? t('edit') : t('create.verb')} ${t(
          'pallet.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <PalletForm
          pallet={pallet}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('pallet.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('pallet.delete')} <q>{currentPallet?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
